import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import LandingPage      from './pages/LandingPage';
import AuthPage         from './pages/AuthPage';
import ProfileSetupPage from './pages/ProfileSetupPage';
import ExplorePage      from './pages/ExplorePage';
import ChatPage         from './pages/ChatPage';
import AdminPage        from './pages/AdminPage';
import SubscriptionPage from './pages/SubscriptionPage';
import RegalosPage      from './pages/RegalosPage';
import AfinidadPage     from './pages/AfinidadPage';
import DescuentosPage   from './pages/DescuentosPage';
import CitaSeguraPublicaPage from './pages/CitaSeguraPublicaPage';
import AyudaPage        from './pages/AyudaPage';
import VerificacionPage from './pages/VerificacionPage';
import ComercioPage     from './pages/ComercioPage';
import AiChatbot        from './components/AiChatbot';
import AppPrompt        from './components/AppPrompt';
import ActivarAvisos    from './components/ActivarAvisos';
import './styles/globals.css';

// Link de influencer: si se entra con ?ref=codigo, se guarda 30 días en una cookie.
// El servidor la lee al registrarse (email o Google) y asocia al usuario con ese influencer.
(function captureRefCode() {
  try {
    const ref = new URLSearchParams(window.location.search).get('ref');
    const code = (ref || '').trim().toLowerCase();
    if (!/^[a-z0-9_-]{3,30}$/.test(code)) return;
    const secure = window.location.protocol === 'https:' ? '; Secure' : '';
    document.cookie = `cupido_ref=${code}; Max-Age=${30 * 24 * 60 * 60}; Path=/; SameSite=Lax${secure}`;
  } catch { /* sin cookies: el registro sigue igual, sin referido */ }
})();

function PrivateRoute({ children }) {
  const { user } = useAuth();
  return user ? children : <Navigate to="/auth" replace />;
}
// Enlaces viejos /descuentos/panel (correos, vuelta de Mercado Pago, app instalada) → /descuentos, con su ?token= o ?pago=
function PanelDescuentosViejo() {
  const { search } = useLocation();
  return <Navigate to={`/descuentos${search}`} replace />;
}
function AdminRoute({ children }) {
  const { user } = useAuth();
  return user?.role === 'admin' ? children : <Navigate to="/explorar" replace />;
}

// Wrapper that shows the AI chatbot on all authenticated pages
function AuthenticatedApp({ children }) {
  const { user } = useAuth();
  const { pathname } = useLocation();
  // En los paneles de comercios va su propio asistente (con o sin sesión): Cupido Delivery o Cupido Descuentos
  const panelDescuentos = pathname.startsWith('/descuentos');
  return (
    <>
      {children}
      {pathname.startsWith('/comercio') ? <AiChatbot key="comercio" modo="comercio" />
        : panelDescuentos ? <AiChatbot key="descuentos" modo="descuentos" />
        : user && <AiChatbot key="usuarios" />}
      {/* Notificaciones de la web: en las pantallas principales (no en un chat abierto, que tapa donde se escribe) */}
      {user?.profileComplete && ['/explorar', '/chat', '/regalos'].includes(pathname) && <ActivarAvisos key={user.id} tipo="usuario" />}
      <AppPrompt />
    </>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <Router>
        <AuthenticatedApp>
          <Routes>
            <Route path="/"                element={<LandingPage />} />
            <Route path="/auth"            element={<AuthPage />} />
            <Route path="/verificar-email" element={<AuthPage />} />
            <Route path="/reset-password"  element={<AuthPage />} />
            <Route path="/perfil/setup"    element={<PrivateRoute><ProfileSetupPage /></PrivateRoute>} />
            <Route path="/explorar"        element={<PrivateRoute><ExplorePage /></PrivateRoute>} />
            <Route path="/chat"            element={<PrivateRoute><ChatPage /></PrivateRoute>} />
            <Route path="/chat/:matchId"   element={<PrivateRoute><ChatPage /></PrivateRoute>} />
            <Route path="/suscripcion"     element={<PrivateRoute><SubscriptionPage /></PrivateRoute>} />
            <Route path="/planes"          element={<PrivateRoute><SubscriptionPage /></PrivateRoute>} />
            <Route path="/pago/exitoso"    element={<PrivateRoute><SubscriptionPage /></PrivateRoute>} />
            <Route path="/pago/fallido"    element={<PrivateRoute><SubscriptionPage /></PrivateRoute>} />
            <Route path="/pago/pendiente"  element={<PrivateRoute><SubscriptionPage /></PrivateRoute>} />
            <Route path="/regalos"         element={<PrivateRoute><RegalosPage /></PrivateRoute>} />
            <Route path="/afinidad"        element={<PrivateRoute><AfinidadPage /></PrivateRoute>} />
            {/* Los usuarios sacan sus códigos en /mis-descuentos; /descuentos es siempre el panel de los locales */}
            <Route path="/mis-descuentos"  element={<PrivateRoute><DescuentosPage /></PrivateRoute>} />
            <Route path="/descuentos"      element={<ComercioPage marca="descuentos" />} />
            <Route path="/descuentos/panel" element={<PanelDescuentosViejo />} />
            <Route path="/verificacion"    element={<PrivateRoute><VerificacionPage /></PrivateRoute>} />
            <Route path="/ayuda"           element={<AyudaPage />} />
            <Route path="/ayuda/:seccion"  element={<AyudaPage />} />
            <Route path="/cita-segura/:token" element={<CitaSeguraPublicaPage />} />
            <Route path="/comercio"        element={<ComercioPage />} />
            <Route path="/comercio/activar" element={<ComercioPage />} />
            <Route path="/admin"           element={<AdminRoute><AdminPage /></AdminRoute>} />
            <Route path="*"               element={<Navigate to="/" replace />} />
          </Routes>
        </AuthenticatedApp>
      </Router>
    </AuthProvider>
  );
}
